import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { KeyRound, Delete, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PinInput = ({ label, value, onChange, hidden = false }) => (
  <div>
    <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
    <div
      className="bg-slate-50 border border-slate-200 rounded-lg h-11 flex items-center justify-center px-3 cursor-pointer"
      onClick={onChange ? () => onChange('focus') : undefined}
    >
      <span className="text-xl font-mono font-bold tracking-[0.25em] text-slate-900">
        {value ? (hidden ? '\u2022'.repeat(value.length) : value) : <span className="text-slate-300 text-sm">----</span>}
      </span>
    </div>
  </div>
);

export const ChangePinDialog = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(0); // 0=current, 1=new, 2=confirm
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);

  const values = [currentPin, newPin, confirmPin];
  const setters = [setCurrentPin, setNewPin, setConfirmPin];
  const labels = ['Current PIN', 'New PIN', 'Confirm New PIN'];

  const handleDigit = (d) => {
    if (values[step].length >= 8) return;
    setters[step](prev => prev + d);
  };

  const handleBackspace = () => {
    setters[step](prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setters[step]('');
  };

  const handleNext = () => {
    if (!values[step] || values[step].length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }
    if (step < 2) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (newPin !== confirmPin) {
      toast.error('New PINs do not match');
      setConfirmPin('');
      setStep(2);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-passcode`, {
        current_passcode: currentPin,
        new_passcode: newPin
      });
      toast.success('PIN changed successfully');
      resetAndClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to change PIN';
      toast.error(msg);
      if (msg.includes('incorrect')) {
        setCurrentPin('');
        setStep(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setCurrentPin('');
    setNewPin('');
    setConfirmPin('');
    setStep(0);
    onOpenChange(false);
  };

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAndClose(); else onOpenChange(v); }}>
      <DialogContent className="max-w-xs p-0 overflow-hidden" data-testid="change-pin-dialog">
        <DialogHeader className="bg-slate-900 px-5 py-4">
          <DialogTitle className="text-white flex items-center gap-2 text-lg">
            <KeyRound className="w-5 h-5" /> Change PIN
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Step {step + 1} of 3: {labels[step]}
          </DialogDescription>
        </DialogHeader>

        <div className="p-5 space-y-4">
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2">
            {[0, 1, 2].map(i => (
              <div key={i} className={`w-2 h-2 rounded-full transition-colors ${
                i === step ? 'bg-slate-900' : i < step ? 'bg-green-500' : 'bg-slate-200'
              }`} />
            ))}
          </div>

          {/* PIN displays */}
          <div className="space-y-2">
            <PinInput label="Current PIN" value={currentPin} hidden />
            {step >= 1 && <PinInput label="New PIN" value={newPin} hidden />}
            {step >= 2 && <PinInput label="Confirm PIN" value={confirmPin} hidden />}
          </div>

          {/* Active step label */}
          <p className="text-center text-sm font-medium text-slate-600" data-testid="pin-step-label">
            {labels[step]}
          </p>

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-1.5">
            {digits.map(d => (
              <button key={d} data-testid={`pin-numpad-${d}`} onClick={() => handleDigit(d)}
                className="h-12 rounded-lg bg-white border border-slate-200 text-lg font-semibold text-slate-800 hover:bg-slate-50 active:bg-slate-100 active:scale-95 transition-all">
                {d}
              </button>
            ))}
            <button data-testid="pin-numpad-clear" onClick={handleClear}
              className="h-12 rounded-lg bg-slate-100 border border-slate-200 text-xs font-medium text-slate-500 hover:bg-slate-200 active:scale-95 transition-all">
              Clear
            </button>
            <button data-testid="pin-numpad-0" onClick={() => handleDigit('0')}
              className="h-12 rounded-lg bg-white border border-slate-200 text-lg font-semibold text-slate-800 hover:bg-slate-50 active:bg-slate-100 active:scale-95 transition-all">
              0
            </button>
            <button data-testid="pin-numpad-backspace" onClick={handleBackspace}
              className="h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-200 active:scale-95 transition-all">
              <Delete className="w-4 h-4" />
            </button>
          </div>

          {/* Action */}
          <Button data-testid="pin-next-btn" className="w-full h-12" onClick={handleNext}
            disabled={loading || !values[step]}>
            {loading ? 'Saving...' : step < 2 ? 'Next' : (
              <span className="flex items-center gap-2"><Check className="w-4 h-4" /> Confirm Change</span>
            )}
          </Button>

          {step > 0 && (
            <button data-testid="pin-back-btn" onClick={() => setStep(step - 1)}
              className="w-full text-sm text-slate-500 hover:text-slate-700 text-center">
              &larr; Back
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
